// Utility functions for SpeedoTube Downloads management
import { API_BASE_URL } from '../config';

export const DOWNLOADS_STORAGE_KEY = 'speedotube_downloaded_videos';
export const DOWNLOAD_SETTINGS_KEY = 'speedotube_download_settings';

export const QUALITY_PRESETS = [
  { id: '1080p', label: 'Full HD (1080p)', shortLabel: '1080p HD', rateMbPerMin: 22 },
  { id: '720p', label: 'High (720p)', shortLabel: '720p HD', rateMbPerMin: 12 },
  { id: '480p', label: 'Medium (480p)', shortLabel: '480p', rateMbPerMin: 7 },
  { id: '360p', label: 'Low (360p)', shortLabel: '360p', rateMbPerMin: 4 },
];

/**
 * Returns all downloaded videos from local storage
 */
export function getDownloadedVideos() {
  try {
    const raw = localStorage.getItem(DOWNLOADS_STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch (e) {
    console.warn('Failed to parse downloaded videos:', e);
    return [];
  }
}

/**
 * Checks if a video ID is currently downloaded
 */
export function isDownloaded(videoId) {
  if (!videoId) return false;
  const list = getDownloadedVideos();
  return list.some((item) => String(item.id) === String(videoId) || String(item.videoUrlId) === String(videoId));
}

/**
 * Retrieves a specific downloaded item
 */
export function getDownloadedItem(videoId) {
  if (!videoId) return null;
  const list = getDownloadedVideos();
  return list.find((item) => String(item.id) === String(videoId) || String(item.videoUrlId) === String(videoId)) || null;
}

/**
 * Formats duration in seconds to mm:ss or hh:mm:ss
 */
function parseDurationSec(durationStr) {
  if (!durationStr) return 180;
  if (typeof durationStr === 'number') return durationStr;
  const parts = durationStr.split(':').map((p) => parseInt(p, 10));
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 180;
}

/**
 * Calculates estimated file size
 */
export function estimateVideoSize(durationSec, quality = '720p') {
  const preset = QUALITY_PRESETS.find((p) => p.id === quality) || QUALITY_PRESETS[1];
  const minutes = Math.max(0.5, (durationSec || 180) / 60);
  const sizeMb = minutes * preset.rateMbPerMin;
  return `${sizeMb.toFixed(1)} MB`;
}

/**
 * Adds or updates a video in the downloads list
 */
export function addDownloadedVideo(video, quality = '720p') {
  if (!video || (!video.id && !video.videoUrlId)) return null;

  const currentList = getDownloadedVideos();
  const videoId = String(video.id || video.videoUrlId);
  const durationSec = parseDurationSec(video.duration);
  const sizeStr = estimateVideoSize(durationSec, quality);
  const preset = QUALITY_PRESETS.find((p) => p.id === quality) || QUALITY_PRESETS[1];

  const newItem = {
    id: videoId,
    videoUrlId: video.videoUrlId || video.id || videoId,
    title: video.title || 'SpeedoTube Video',
    channelName: video.channelName || video.channel_name || 'Speedo Creator',
    channelAvatar: video.channelAvatar || video.channel_avatar || null,
    thumbnailUrl: video.thumbnailUrl || video.thumbnail_url || `${API_BASE_URL}/drive/video_thumbnails/thumbnail-157.jpeg`,
    duration: video.duration || '2:53',
    views: video.views || '1K views',
    quality: preset.shortLabel,
    qualityId: preset.id,
    size: sizeStr,
    sizeMb: parseFloat(sizeStr),
    downloadedAt: new Date().toISOString(),
    expireDaysRemaining: 29,
  };

  const filtered = currentList.filter((item) => String(item.id) !== videoId && String(item.videoUrlId) !== videoId);
  filtered.unshift(newItem);

  try {
    localStorage.setItem(DOWNLOADS_STORAGE_KEY, JSON.stringify(filtered));
    window.dispatchEvent(new Event('speedotube_downloads_updated'));
  } catch (e) {
    console.error('Failed to save download:', e);
  }

  return newItem;
}

/**
 * Removes a video from downloads
 */
export function removeDownloadedVideo(videoId) {
  if (!videoId) return;
  const currentList = getDownloadedVideos();
  const targetId = String(videoId);
  const updated = currentList.filter((item) => String(item.id) !== targetId && String(item.videoUrlId) !== targetId);

  try {
    localStorage.setItem(DOWNLOADS_STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new Event('speedotube_downloads_updated'));
  } catch (e) {
    console.error('Failed to remove download:', e);
  }
}

/**
 * Clears all downloaded videos
 */
export function clearAllDownloads() {
  try {
    localStorage.setItem(DOWNLOADS_STORAGE_KEY, JSON.stringify([]));
    window.dispatchEvent(new Event('speedotube_downloads_updated'));
  } catch (e) {
    console.error('Failed to clear downloads:', e);
  }
}

/**
 * Retrieves download settings
 */
export function getDownloadSettings() {
  try {
    const raw = localStorage.getItem(DOWNLOAD_SETTINGS_KEY);
    if (!raw) {
      return {
        defaultQuality: 'ask', // 'ask', '1080p', '720p', '480p', '360p'
        smartDownloads: true,
        downloadOverWifiOnly: false,
      };
    }
    return JSON.parse(raw);
  } catch (e) {
    return {
      defaultQuality: 'ask',
      smartDownloads: true,
      downloadOverWifiOnly: false,
    };
  }
}

/**
 * Saves download settings
 */
export function saveDownloadSettings(settings) {
  try {
    localStorage.setItem(DOWNLOAD_SETTINGS_KEY, JSON.stringify(settings));
    window.dispatchEvent(new Event('speedotube_download_settings_updated'));
  } catch (e) {
    console.error('Failed to save download settings:', e);
  }
}

/**
 * Calculates storage summary
 */
export function calculateStorageStats(videos = []) {
  const totalMb = videos.reduce((acc, curr) => acc + (curr.sizeMb || 20), 0);
  let usedFormatted = '';
  if (totalMb >= 1024) {
    usedFormatted = `${(totalMb / 1024).toFixed(2)} GB`;
  } else {
    usedFormatted = `${totalMb.toFixed(1)} MB`;
  }

  return {
    totalMb,
    usedFormatted,
    freeFormatted: '124.5 GB free',
    percentageUsed: Math.min(100, Math.max(1, (totalMb / (124.5 * 1024)) * 100)).toFixed(1),
  };
}
